import FloatingWhatsAppButton from './FloatingWhatsAppButton'
import RogerioSpeech from './RogerioSpeech'
import SiteFooter from './SiteFooter'
import SiteHeader from './SiteHeader'
import OficinaWorld from './OficinaWorld'
import PageOpening from './PageOpening'
import CampaignVideo from './CampaignVideo'
import { empreendedoresFaqItems } from './seo/empreendedoresPage'
import { bookingLabel, empreendedoresWhatsAppMessage, individualBookingUrl, potencialPath, whatsAppUrlFor, youtubeChannelUrl } from './siteDestinations'
import { ActionLink, PaperCard, SectionHeading, ThreadDivider, WorkshopGlyph } from './ui/Workshop' // termo-ok: nome técnico do arquivo de componentes
import './empreendedores.css'

const contactUrl = whatsAppUrlFor(empreendedoresWhatsAppMessage)

/** A dor de quem ainda não começou, dita sem peso: cada frase afirma o que é. */
const recognition = [
  'Você vê concorrente, cliente e até funcionário usando IA. E ainda não sabe por onde começar no seu caso.',
  'O que falta é ver a IA aplicada ao seu negócio, nas tarefas que você já faz.',
  'O que conta é saber do que o seu negócio precisa. E isso você já sabe.',
  'Sua experiência dá a direção à IA. Ela faz o trabalho pesado. Quem decide é você.',
]

/** Tarefa real e o que melhora com ela; sem número de faturamento. */
const examples = [
  {
    title: 'Responda mais rápido.',
    description: 'E-mails e mensagens de clientes e fornecedores, no seu tom.',
    request: 'Tenho 20 e-mails de clientes para responder. Pode me ajudar a rascunhar as respostas?',
    kind: 'person',
    tone: 'peach',
  },
  {
    title: 'Proposta sem começar do zero.',
    description: 'Orçamento e proposta a partir das suas anotações.',
    request: 'Anotei o que o cliente pediu. Monte uma proposta com esses itens e valores.',
    kind: 'notes',
    tone: 'sky',
  },
  {
    title: 'Leia menos, entenda mais.',
    description: 'Resumo de contrato, relatório ou planilha longa.',
    request: 'Resuma este contrato e me diga o que merece atenção.',
    kind: 'notes',
    tone: 'sage',
  },
  {
    title: 'Números em ordem.',
    description: 'Planilha de clientes, vendas e cobranças.',
    request: 'Organize minhas vendas do mês por cliente e mostre quem está atrasado.',
    kind: 'numbers',
    tone: 'peach',
  },
  {
    title: 'A rotina que anda sozinha.',
    description: 'Tarefas que se repetem toda semana.',
    request: 'Toda segunda eu monto o mesmo relatório. Dá para deixar isso pronto?',
    kind: 'thread',
    tone: 'sky',
    more: { href: potencialPath, label: 'Ver mais exemplos do que dá para fazer com IA' },
  },
] as const

/** A IA escreve o código a partir de um pedido em português. */
const building = [
  { title: 'Você descreve.', text: 'Em português, do jeito que você explicaria para alguém da equipe.' },
  { title: 'A IA escreve o código.', text: 'Ela monta a ferramenta ou a rotina a partir do seu pedido.' },
  { title: 'Você confere.', text: 'Testa, pede ajustes e decide o que vai usar no negócio.' },
]

const tools = [
  {
    title: 'Uma ferramenta para o seu negócio.',
    description: 'Uma calculadora de orçamento ou um cadastro de clientes, feitos a partir de um pedido em português.',
    request: 'Quero uma calculadora de orçamento: eu escolho os serviços e ela soma o total com o meu preço.',
    kind: 'numbers',
    tone: 'sage',
  },
  {
    title: 'Uma rotina que roda no computador.',
    description: 'Renomear e organizar arquivos em pastas, montar o relatório da semana.',
    request: 'Toda sexta eu separo as notas fiscais em pastas por mês. Dá para o computador fazer isso?',
    kind: 'thread',
    tone: 'peach',
  },
] as const

const steps = [
  { title: 'Partimos de uma tarefa do seu negócio.', text: 'Algo que você faz toda semana, que toma o seu tempo ou que você sempre quis resolver.' },
  { title: 'Fazemos um passo de cada vez.', text: 'Nos encontros individuais, eu explico o caminho, praticamos juntos e você pode trazer suas perguntas.' },
  { title: 'Você confere e pode repetir.', text: 'Aprender a ajustar a resposta, verificar as informações e guardar o caminho para usar de novo no negócio.' },
]

/** Página de venda para empreendedores: acompanhamento individual a partir de uma primeira conversa gratuita. */
export default function EmpreendedoresPage() {
  return <div className="landing emp-landing" id="topo">
    <OficinaWorld />
    <a className="skip-link" href="#conteudo">Pular para o conteúdo</a>

    <SiteHeader />

    <main id="conteudo" tabIndex={-1}>
      <PageOpening
        eyebrow="Oficina IA para empreendedores · acompanhamento individual"
        title={<>Você sabe que a IA ajuda. <em>Falta saber como no seu negócio.</em></>}
        intro="Acompanhamento individual para quem construiu o negócio sem IA e quer começar agora. A gente parte das tarefas que você já faz no computador e descobre, juntos, onde a IA entra. Eu sou o Arthur."
        rogerio={{ pose: 'curioso', text: 'Uma tarefa do seu negócio já é um bom começo.' }}
        facts={[[ '10+', 'anos ensinando adultos' ], [ '30 min', 'de primeira conversa gratuita' ], [ 'Seu negócio', 'como ponto de partida' ], [ '9 h', 'de acompanhamento individual pago' ]]}
        flow={['conversar', 'escolher uma tarefa', 'fazer junto', 'perguntar', 'conferir', 'aplicar no negócio', 'seguir sozinho']}
        secondary={{ href: '#no-seu-negocio', label: 'Ver o que muda' }}
      />
      <div className="of-folha">
      <section className="emp-section emp-recognition" id="comeco" aria-labelledby="emp-recognition-title">
        <SectionHeading eyebrow="Se você se reconhece aqui" title={<span id="emp-recognition-title">Dá para começar agora.</span>} />
        <ul className="emp-recognition__list">
          {recognition.map((text) => <li key={text}>{text}</li>)}
        </ul>
      </section>

      <section className="emp-section" id="no-seu-negocio" aria-labelledby="emp-examples-title">
        <SectionHeading eyebrow="Do seu negócio para a Oficina" title={<span id="emp-examples-title">O que muda no seu negócio.</span>}>
          Tarefas que você já faz, com a IA ajudando. Cada uma devolve um pouco do seu tempo.
        </SectionHeading>
        <div className="emp-examples">
          {examples.map((example) => <PaperCard key={example.title} tone={example.tone} className="emp-example">
            <WorkshopGlyph kind={example.kind} />
            <h3>{example.title}</h3>
            <p>{example.description}</p>
            <div className="emp-example__request">
              <p>Você poderia pedir:</p>
              <blockquote>“{example.request}”</blockquote>
              {'more' in example && <a className="emp-example__more" href={example.more.href}>{example.more.label} <span aria-hidden="true">→</span></a>}
            </div>
          </PaperCard>)}
        </div>
        <p className="emp-examples-note">Você dá a direção. A IA sugere. Você aprende a conferir e escolher o que vai usar.</p>
      </section>

      <section className="emp-section emp-building" id="programar" aria-labelledby="emp-building-title">
        <SectionHeading eyebrow="Além de texto e planilha" title={<span id="emp-building-title">Não sabe programar? Não precisa.</span>}>
          A IA programa por você. Você descreve em português o que quer, ela escreve o código e você confere o resultado.
        </SectionHeading>
        <ol className="emp-building__flow">
          {building.map((step, index) => <li key={step.title}>
            <span className="emp-step-number" aria-hidden="true">0{index + 1}</span>
            <div><h3>{step.title}</h3><p>{step.text}</p></div>
          </li>)}
        </ol>
        <div className="emp-examples emp-examples--two">
          {tools.map((tool) => <PaperCard key={tool.title} tone={tool.tone} className="emp-example">
            <WorkshopGlyph kind={tool.kind} />
            <h3>{tool.title}</h3>
            <p>{tool.description}</p>
            <div className="emp-example__request">
              <p>Você poderia pedir:</p>
              <blockquote>“{tool.request}”</blockquote>
            </div>
          </PaperCard>)}
        </div>
        <RogerioSpeech pose="concluido" layout="beside">Ah! e o Arthur fez esse site todinho com IA</RogerioSpeech>
      </section>

      <section className="emp-method" id="como-funciona" aria-labelledby="emp-method-title">
        <div className="emp-section emp-method__inner">
          <div>
            <SectionHeading eyebrow="Como vamos aprender" title={<span id="emp-method-title">Um passo de cada vez. <br />Com você participando.</span>} />
            <ol className="emp-steps">
              {steps.map((step, index) => <li key={step.title}>
                <span className="emp-step-number" aria-hidden="true">0{index + 1}</span>
                <div><h3>{step.title}</h3><p>{step.text}</p></div>
              </li>)}
            </ol>
            <p className="emp-method__goal">O objetivo das 9 horas: você usar IA sozinho nas tarefas do seu negócio.</p>
          </div>
          <PaperCard className="emp-start-note">
            <p className="eyebrow">O seu ponto de partida</p>
            <h3>Qual tarefa toma <br />o seu tempo?</h3>
            <p>Me conte uma tarefa do seu negócio que você faz no computador e gostaria de facilitar com IA. Juntos, vemos por onde começar.</p>
            <p>Você pode me contar o que já faz, o que gostaria de aprender e onde precisa de ajuda.</p>
            <RogerioSpeech pose="pedindo-esclarecimento" layout="beside">Tem uma pergunta? Ela também pode ser o começo.</RogerioSpeech>
          </PaperCard>
        </div>
      </section>

      <section className="emp-section emp-about" id="arthur" aria-labelledby="emp-about-title">
        <div>
          <SectionHeading eyebrow="Prazer, eu sou o Arthur" title={<span id="emp-about-title">Ensinar adultos faz parte da minha história.</span>} />
          <p>Ensino idiomas para adultos há mais de dez anos. Criei a Oficina IA para ajudar pessoas a usar inteligência artificial em tarefas reais e continuar por conta própria.</p>
          <p>No acompanhamento individual, eu parto do seu negócio e explico cada etapa com clareza. Aprender também é experimentar, perguntar e tentar de novo.</p>
          <ActionLink variant="secondary" href={youtubeChannelUrl} target="_blank" rel="noopener noreferrer">Conhecer minhas aulas gratuitas</ActionLink>
          <p className="emp-note">Abre o canal da Oficina IA no YouTube, onde você pode conhecer meu jeito de explicar.</p>
        </div>
        <div className="emp-about__scene">
          <CampaignVideo />
        </div>
      </section>

      <section className="emp-invitation" id="acompanhamento" aria-labelledby="emp-invitation-title">
        <div className="emp-section emp-invitation__inner">
          <div>
            <span className="of-badge">Acompanhamento individual</span>
            <h2 id="emp-invitation-title">Onde a IA entra <br />no seu negócio?</h2>
            <p>Na primeira conversa, de 30 minutos e gratuita, a gente olha o seu negócio e descobre onde a IA entra primeiro.</p>
            <p>Você sai sabendo por onde começar.</p>
          </div>
          <div className="emp-invitation__action">
            <ActionLink href={individualBookingUrl} target="_blank" rel="noopener noreferrer">{bookingLabel}</ActionLink>
            <p className="emp-note">Você escolhe o horário na minha agenda.</p>
            <div className="emp-invitation__questions">
              <p>Quer tirar uma dúvida antes?</p>
              <a href={contactUrl} target="_blank" rel="noopener noreferrer">Falar comigo no WhatsApp <span aria-hidden="true">↗</span></a>
            </div>
          </div>
        </div>
      </section>

      <section className="emp-section emp-faq" id="duvidas" aria-labelledby="emp-faq-title">
        <div>
          <SectionHeading eyebrow="Antes de começar" title={<span id="emp-faq-title">Pode perguntar.</span>} />
          <p>Estas respostas ajudam a entender o acompanhamento. Se ficar alguma dúvida, <a href={contactUrl} target="_blank" rel="noopener noreferrer">fale comigo no WhatsApp</a>.</p>
        </div>
        <div className="emp-faq-list">
          {empreendedoresFaqItems.map((item, index) => <details key={item.question} open={index === 0}>
            <summary>{item.question}</summary>
            <p>{item.answer}</p>
          </details>)}
        </div>
      </section>
      <div className="emp-thread"><ThreadDivider /></div>
      </div>
    </main>

    <FloatingWhatsAppButton />
    <SiteFooter />
  </div>
}
